import { computed } from 'vue'
import { useCategoryByPath } from '#storefront/composables'
import { useRoute } from '#app/composables/router'
import useCms from '~/composables/cms/useCms'
import type {
  SbAdvantagePage,
  SbContentPage,
} from '~/cms/storyblok/types/storyblok'

/**
 * Route prefixes that have explicit page handlers and should NOT use catch-all prefetch.
 * These correspond to files/folders in the pages/ directory.
 */
const EXPLICIT_ROUTE_PREFIXES = [
  '/account',
  '/basket',
  '/c/',
  '/checkout',
  '/magazine',
  '/outfits',
  '/p/',
  '/password',
  '/search',
  '/service',
  '/signin',
  '/success',
  '/wishlist',
]

/**
 * Valid CMS component types that can be rendered by the catch-all page.
 */
const VALID_CMS_COMPONENTS = ['ContentPage', 'AdvantagePage']

/**
 * Checks if the current route will be handled by the catch-all [...slug].vue page.
 */
function isCatchAllRoute(path: string): boolean {
  // Root path has its own index.vue
  if (path === '/' || path === '') {
    return false
  }

  // Check if path starts with any explicit route prefix
  return !EXPLICIT_ROUTE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix),
  )
}

/**
 * Extracts the slug from route params, matching exactly what [...slug].vue does.
 */
function extractSlugFromParams(params: Record<string, unknown>): string | null {
  const slugParam = params.slug
  if (!slugParam) return null

  // Match the exact logic from [...slug].vue
  return Array.isArray(slugParam) ? slugParam.join('/') : (slugParam as string)
}

/**
 * Composable for catch-all route data fetching.
 *
 * Used by both:
 * - Layout: calls prefetch() to load data in parallel with other layout fetches
 * - Catch-all page: uses the returned data refs and fetch methods
 *
 * Data is shared via useState keys, so layout prefetch populates the same
 * state that the page reads from.
 *
 * ECOM-2692 note: the layout's `prefetchCatchAllData` and this composable share
 * the `category-by-path-${slug}` state. On a fresh load the layout seeds it for
 * the *reloaded* route; a browser-back to another route before hydration can
 * leave that state stale for the active route. The PLP reconciliation
 * (`usePlpNavigationReconcile`) is the safety net that re-fetches the active
 * route's data — do not treat layout-seeded category state as authoritative for
 * the live route.
 */
export default function useCatchAllPrefetch() {
  const route = useRoute()
  const path = computed(() => route.path)
  const slug = computed(() => extractSlugFromParams(route.params))

  // Category data - used to determine if this is a PLP
  const category = useCategoryByPath(
    {
      params: { path: slug.value || '' },
      options: {
        immediate: false,
      },
    },
    `category-by-path-${slug.value}`,
  )

  // CMS data - used for content pages
  const {
    data: cmsData,
    fetching: cmsFetching,
    fetchBySlug,
    excluded,
  } = useCms<SbContentPage | SbAdvantagePage>(`cms-content-page-${path.value}`)

  // Page type detection.
  //
  // ECOM-2692: use truthiness, not `!== null`. `useCategoryByPath` resolves to
  // `undefined` (not `null`) when category data was never fetched — and during
  // the browser-back hydration race the target route's category is exactly that:
  // never fetched. `undefined !== null` is `true`, so the old check rendered the
  // PLP shell for an unresolved category (empty title, no products) instead of
  // fetching or 404ing. `Boolean(...)` treats both `undefined` (never fetched)
  // and `null` (explicit no-match) as not-a-PLP. This also subsumes the parallel
  // fix on main for CMS content pages (e.g. draft Storyblok pages in preview)
  // being misclassified as PLPs and 404ing.
  //
  // Hydration-safe: this only changes classification for the `undefined` case,
  // which by definition was never SSR-resolved (the broken route is reached by
  // client-side back-nav after a fresh load on a *different* route), so there is
  // no server markup for it to mismatch against. The `null` and real-object
  // cases behave identically under `!== null` and `Boolean(...)`.
  const isPLP = computed(() => Boolean(category.data.value))

  const cmsComponentType = computed(() => cmsData.value?.content?.component)

  const isValidCmsComponent = computed(() =>
    VALID_CMS_COMPONENTS.includes(cmsComponentType.value || ''),
  )

  // Same undefined-vs-null discipline as isPLP (ECOM-2692): `useCms` data is
  // `undefined` until fetched. `isValidCmsComponent` already guards via optional
  // chaining, but use `Boolean(...)` here too so the absence check is consistent
  // and a never-fetched CMS payload is never mistaken for present.
  const isContentPage = computed(
    () => !isPLP.value && Boolean(cmsData.value) && isValidCmsComponent.value,
  )

  // The validated content - only set if it's a valid content page
  const content = computed(() =>
    isValidCmsComponent.value ? cmsData.value?.content : null,
  )

  // Page type for definePageMeta
  const pageType = computed<'content' | 'category' | null>(() => {
    if (isContentPage.value) return 'content'
    if (isPLP.value) return 'category'
    return null
  })

  /**
   * Prefetch data for catch-all routes. Call from layout to parallelize.
   * Returns early for non-catch-all routes.
   */
  const prefetch = async () => {
    if (!isCatchAllRoute(path.value)) {
      return { skipped: true }
    }

    if (!slug.value) {
      return { skipped: true, reason: 'no-slug' }
    }

    // Fetch category first to determine if it's a PLP
    await category.refresh()

    // If it's a category (PLP), we're done - no need to fetch CMS content
    if (category.data.value) {
      return { isPLP: true }
    }

    // Not a PLP - fetch CMS content
    await fetchBySlug(path.value)

    return { isPLP: false }
  }

  /**
   * Refresh category data. Used by catch-all page if not prefetched.
   */
  const refreshCategory = () => category.refresh()

  /**
   * Fetch CMS content by slug. Used by catch-all page if not prefetched.
   */
  const fetchCmsContent = (slug: string) => fetchBySlug(slug)

  return {
    // Data refs
    category,
    categoryData: category.data,
    cmsData,
    cmsFetching,
    cmsExcluded: excluded,
    slug,
    path,

    // Page type detection
    isPLP,
    isContentPage,
    content,
    pageType,

    // Methods
    prefetch,
    refreshCategory,
    fetchCmsContent,

    // Utilities
    isCatchAllRoute: () => isCatchAllRoute(path.value),
  }
}
